// src/cockpit4/components/DealMakerPanel.tsx
// ============================================
// V1.5 P1-6：AI Deal Maker（决策式成交卡）
//
// 理念：CRM 不再是"客户是谁"，而是"钱怎么从这个客户身上出来"。
// 选中一个客户，系统直接给出：
//   客户痛点 → 成交概率 → 推荐产品/报价 → 切入点 → 今日只做一件事
//
// 数据来源（全部真实，无假数据）：
//   - 客户：RuntimeManager.query(CRM, list) → CustomerProfile（痛点取 history/analysis 字段）
//   - 推荐产品/报价：RuntimeManager.query(SKU, list) → 按客户行业/标签/沟通文本匹配 SKU
//   - 成交概率：客户 tier 权重 + 业务阶段 + 档案完整度（规则化，不烧 Token）
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target,
  Loader2,
  RefreshCw,
  Users,
  Wallet,
  Crosshair,
  Footprints,
  ArrowRight,
  AlertTriangle,
  Package,
  CheckCircle2,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { SKUQuery, type SKUProfile } from "../../runtime/SKURuntime/types";
import type { CustomerProfile, CustomerTier, CustomerCategory } from "../../runtime/CRMRuntime/types";

// ============================================
// 规则化成交概率
// ============================================

/** 客户等级权重（决定成交概率基准分） */
const TIER_BASE: Record<CustomerTier, number> = {
  "S+": 70,
  S: 65,
  A: 58,
  B: 45,
  C: 32,
};

/** 业务阶段加成（越接近成交越高） */
const CATEGORY_BONUS: Record<CustomerCategory, number> = {
  hotel: 0,
  ai: 0,
  potential: 5,
  tracking: 18,
  active: 25,
  other: 0,
};

/** 千分位格式化 */
function fmt(v: number): string {
  return v.toLocaleString("zh-CN");
}

export default function DealMakerPanel() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [skus, setSkus] = useState<SKUProfile[]>([]);
  const [customerId, setCustomerId] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const init = RuntimeManager.isInitialized();
      const [crmOk, skuOk] = [
        init && RuntimeManager.hasRuntime(RuntimeName.CRM),
        init && RuntimeManager.hasRuntime(RuntimeName.SKU),
      ];
      const [crmResp, skuResp] = await Promise.all([
        crmOk
          ? RuntimeManager.query(RuntimeName.CRM, { type: "list" } as any).catch(() => null)
          : Promise.resolve(null),
        skuOk
          ? RuntimeManager.query(RuntimeName.SKU, { type: SKUQuery.List } as any).catch(() => null)
          : Promise.resolve(null),
      ]);
      const crmData = crmResp?.data ?? crmResp ?? [];
      const skuData = skuResp?.data ?? skuResp ?? [];
      setCustomers(Array.isArray(crmData) ? crmData : []);
      setSkus(Array.isArray(skuData) ? skuData : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const customer = useMemo(
    () => customers.find((c) => c.id === customerId) || null,
    [customers, customerId]
  );

  // ============ 成交卡计算（规则化） ============

  /** 客户痛点（优先 analysis，其次 history，最后 tags） */
  const pains = useMemo(() => {
    if (!customer) return [] as string[];
    const lines: string[] = [];
    const analysis = customer.analysis || "";
    const history = customer.history || "";
    // 从档案文本中切出痛点候选（按关键词归并，最多 3 条）
    const sources = [analysis, history].join("\n");
    const patterns: Array<[RegExp, string]> = [
      [/不会做|不会拍|不懂|缺乏|没时间|没精力/g, "不会做 / 没精力做（人力短板）"],
      [/没人维护|没有运营|缺人/g, "无人维护运营（缺人手）"],
      [/获客|流量|拓客|引流|线索/g, "获客难 / 流量不足"],
      [/转化|成交|询价|意向/g, "询价多但转化难"],
      [/成本|贵|预算/g, "成本敏感 / 预算有限"],
      [/朋友圈/g, "朋友圈内容荒"],
      [/短视频|抖音|视频/g, "短视频不会做"],
      [/AI|智能|自动/g, "想上 AI 但不知从哪入手"],
    ];
    for (const [re, label] of patterns) {
      if (re.test(sources) && lines.length < 3) lines.push(label);
    }
    if (lines.length === 0 && (analysis || history)) lines.push("档案待完善（历史沟通较少，建议先电话建联挖痛点）");
    return lines;
  }, [customer]);

  /** 成交概率 */
  const probability = useMemo(() => {
    if (!customer) return 0;
    let p = TIER_BASE[customer.tier] ?? 45;
    p += CATEGORY_BONUS[customer.category] ?? 0;
    if (customer.analysis) p += 4; // 档案分析越完整，判断越可靠
    if (customer.history) p += 3;
    if (customer.followup?.nextContact) p += 3; // 已排下次跟进 → 高意向
    if (customer.followup.estimatedValue > 0) p += 3; // 有预估成交金额 → 已进入实质阶段
    return Math.min(92, Math.max(20, p));
  }, [customer]);

  /** 推荐产品（按行业/标签/沟通文本匹配 SKU 库） */
  const recommendedSku = useMemo(() => {
    if (!customer || skus.length === 0) return null;
    const haystack = [customer.industry, ...(customer.tags || []), customer.analysis, customer.history]
      .filter(Boolean)
      .join(" ");
    // 命中评分：SKU 名称/适合场景/卖点包含客户关键词
    const scored = skus
      .map((sku) => {
        const target = [sku.name, ...(sku.suitableFor || []), sku.sellingPoints].join(" ");
        let score = 0;
        for (const kw of customer.tags || []) {
          if (target.includes(kw)) score += 3;
        }
        if (target.includes(customer.industry)) score += 2;
        return { sku, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);
    return scored[0]?.sku || null;
  }, [customer, skus]);

  /** 切入点（基于痛点，免费钩子） */
  const hook = useMemo(() => {
    if (!customer) return "";
    const all = [customer.analysis, customer.history, customer.industry].join(" ");
    if (/朋友圈/.test(all)) return "免费给他生成一套酒店/行业朋友圈内容（3 条 demo）";
    if (/短视频|抖音|视频/.test(all)) return "免费出一套行业短视频脚本（含镜头拆解）";
    if (/知识库|文档|培训/.test(all)) return "免费搭一个企业 AI 知识库 demo（30 分钟演示）";
    if (/获客|流量|线索/.test(all)) return "免费出一份行业获客诊断（GEO 基线报告）";
    return "发一份同行业成功案例（已验证的成交案例最打动）";
  }, [customer]);

  const colorOfProb = (p: number) => (p >= 65 ? "#34D399" : p >= 45 ? "#FBBF24" : "#F87171");

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-4"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-rose-500/50 to-transparent" />

      {/* 标题行 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-md flex items-center justify-center bg-rose-500/15 border border-rose-500/30">
            <Target size={13} className="text-rose-400" />
          </span>
          <div>
            <span className="text-[12px] font-semibold text-white tracking-tight">
              AI Deal Maker
            </span>
            <span className="ml-2 text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              Pain → Probability → Product → Hook → Next Step
            </span>
          </div>
        </div>
        <button
          onClick={() => void loadData()}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          title="刷新客户与产品数据"
        >
          <RefreshCw size={11} className="text-gray-400" />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">刷新</span>
        </button>
      </div>

      {/* 客户选择 */}
      <div className="relative mb-3">
        <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-md border border-white/[0.08] bg-white/[0.02]">
          <Users size={11} className="text-rose-400 flex-shrink-0" />
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="flex-1 bg-transparent text-[11px] font-mono text-gray-200 outline-none cursor-pointer appearance-none"
            title="选择客户 → 生成成交决策卡"
          >
            <option value="">选择客户查看成交决策</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company || c.contact.name}（{c.industry || "未知行业"} · {c.tier} 级）
              </option>
            ))}
          </select>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="grid grid-cols-2 gap-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="rounded-xl border border-white/[0.04] bg-white/[0.01] p-3 animate-pulse">
                  <div className="h-3 w-2/3 bg-white/10 rounded mb-2" />
                  <div className="h-2 w-full bg-white/5 rounded" />
                </div>
              ))}
            </div>
          </motion.div>
        ) : !customer ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="rounded-xl border border-dashed border-white/[0.08] px-4 py-6 text-center"
          >
            <div className="text-[11px] font-mono text-gray-500">
              {customers.length === 0
                ? "暂无客户数据 — 到客户中心建档后，AI 才能告诉你「钱该从哪个客户身上出来」"
                : "选择上方客户 → 系统输出痛点 / 成交概率 / 推荐产品与报价 / 今日一步"}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={customer.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2.5"
          >
            {/* 客户标题 + 成交概率 */}
            <div className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-rose-500/15 border border-rose-500/30 flex items-center justify-center">
                  <Users size={13} className="text-rose-400" />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-white">
                    {customer.company || customer.contact.name}
                  </div>
                  <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                    {customer.industry || "未知行业"} · {customer.region || "未知地区"} · 来源 {customer.source || "未知"}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[20px] font-bold font-mono" style={{ color: colorOfProb(probability) }}>
                  {probability}%
                </div>
                <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">成交概率</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* 痛点 */}
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertTriangle size={10} className="text-amber-400" />
                  <span className="text-[10px] font-semibold text-white">客户痛点</span>
                </div>
                <div className="flex flex-col gap-1">
                  {pains.map((p, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[10px] font-mono text-gray-300">
                      <span className="text-amber-400/70 mt-px">•</span>
                      <span>{p}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 推荐产品 */}
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Package size={10} className="text-blue-400" />
                  <span className="text-[10px] font-semibold text-white">推荐产品 / 报价</span>
                </div>
                {recommendedSku ? (
                  <>
                    <div className="text-[11px] font-medium text-blue-200">{recommendedSku.name}</div>
                    <div className="flex items-center gap-1 text-[10px] font-mono text-gray-300 mt-1">
                      <Wallet size={9} className="text-blue-400" />
                      建议报价 ¥{fmt(recommendedSku.price)}
                      <span className="text-gray-600">· 类目 {recommendedSku.category}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-[10px] font-mono text-gray-500">
                    产品库暂无匹配（SKU 未命中客户行业或标签），可到「Offer 产品设计」按咨询定制
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* 切入点 */}
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Crosshair size={10} className="text-emerald-400" />
                  <span className="text-[10px] font-semibold text-white">切入点（免费钩子）</span>
                </div>
                <p className="text-[10px] font-mono text-emerald-200/90 leading-relaxed">{hook}</p>
              </div>

              {/* 今日一步 */}
              <div className="rounded-xl border border-rose-500/20 bg-rose-500/[0.04] p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Footprints size={10} className="text-rose-400" />
                  <span className="text-[10px] font-semibold text-white">今日只做一件事</span>
                </div>
                <p className="text-[10px] font-mono text-rose-200/90 leading-relaxed">
                  发送案例 → 询问需求 → 给方案 → 报价
                  <span className="text-gray-500">（下一步先做「{hook.slice(0, 18)}…」）</span>
                </p>
                <div className="flex items-center gap-1 mt-2 text-[9px] font-mono text-gray-500">
                  <CheckCircle2 size={9} className="text-rose-400/60" />
                  完成这一步后，到「跟进助手 / 报价谈判」推进下一步
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}
